import { Dispatch, SetStateAction } from 'react'
import { Button } from '@/components/Button'
import { Modal } from '@/components/Modal'
import { BulkDeleteInvoiceType } from '@/api/invoicing/schema'
import { AxiosError } from 'axios'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { bulkDeleteInvoices } from '@/api/invoicing'
import { useToast } from '@/hooks/useToast'
import { HiOutlineExclamationTriangle } from 'react-icons/hi2'

interface DeleteInvoicesModalProps {
    open: boolean
    setOpen: Dispatch<SetStateAction<boolean>>
    invoices: BulkDeleteInvoiceType | null
}

const DeleteInvoicesModal: React.FC<DeleteInvoicesModalProps> = ({ open, setOpen, invoices }) => {
    const { toast } = useToast()
    const queryClient = useQueryClient()

    const { mutate: deleteInvoicesMu } = useMutation<unknown, AxiosError, BulkDeleteInvoiceType>({
        mutationFn: (data: BulkDeleteInvoiceType) => bulkDeleteInvoices(data),
        onSuccess: () => {
            toast({
                description: 'Sucessfully Deleted',
                variant: 'default',
            })

            queryClient.invalidateQueries({ queryKey: ['InvoiceType'] })

            setOpen(false)
        },
        onError: () => {
            toast({
                description: 'Unable to delete paid invoices',
                variant: 'destructive',
            })
        },
    })

    const handleDelete = () => {
        invoices && deleteInvoicesMu(invoices)
    }

    return (
        <Modal
            isOpen={open}
            isHideCloseButton
            onClose={() => {
                setOpen(false)
            }}
            title=''
            titleClassName=''
            containerClassName='max-w-[600px]'
        >
            <div className='flex gap-5 px-10'>
                <div className='flex flex-row gap-5'>
                    <div className='flex justify-center items-center bg-zentive-red-light h-16 w-16 rounded-full'>
                        <HiOutlineExclamationTriangle className='h-6 w-6 mx-5  yx-5 text-red-500' />
                    </div>
                    <div className='flex flex-col'>
                        <h1 className='text-left font-semibold text-2xl text-gray-900 mt-2.5 capitalize'>
                            Delete Invoices
                        </h1>
                        <p className='text-left text-sm text-gray-500 mt-5'>
                            Are you sure you want to delete the selected invoices?
                        </p>
                    </div>
                </div>
            </div>
            <div className='mt-6 flex justify-end gap-x-4 bg-gray-300 py-6 px-6'>
                <Button
                    onClick={() => setOpen(false)}
                    className='w-97 h-11 text-base font-semibold cursor-pointer'
                    variant={'outline'}
                >
                    Cancel
                </Button>

                <Button
                    onClick={handleDelete}
                    className='w-97 h-11 text-base font-semibold bg-zentive-green-dark hover:bg-zentive-green-medium cursor-pointer'
                >
                    Delete
                </Button>
            </div>
        </Modal>
    )
}

export default DeleteInvoicesModal
